import React, { useEffect, useRef, useState } from "react";
import { ResponsivePie } from "@nivo/pie";
import { PieContainer, TooltipBox } from "./PieChart.styled";
import { ThemeProvider, SvgWrapper } from "@nivo/core";
import { BoxLegendSvg } from "@nivo/legends";
import { formatIntlNumber } from "../../../../../utils/Helpers";
import logger from "../../../../../utils/LoggerFrontend";

/**
 * Renders a pie chart to visualize candidate ratings
 *
 * @component
 * @param {Object} props - Component properties
 * @param {Array<{label: string, value: number}>} props.arrayData -
 *  Array of stage objects with:
 *    - `label`: label of the slice (string)
 *    - `value`: percentage of the pie (number)
 * @example
 * const arrayData = [
 *     { label: "Ausgezeichnet", value: 8 },
 *     { label: "Gut", value: 6 },
 *     { label: "Durchschnitt", value: 2 },
 *     { label: "Weniger geeignet", value: 7 },
 *     { label: "Nicht passend", value: 2 },
 *     { label: "No info", value: 1 }
 * ]
 *
 * @returns {React.ReactElement} Pie chart visualization
 */
export const PieChartLegend = ({
  arrayData,
  language = "de",
  averageRating,
  totalAmount,
  transformFunc,
  styling,
  t,
  defaultSizes = { height: 300, width: 600 }
}) => {
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState(defaultSizes);

  logger.debug("PieChartLegend", "render", {
    arrayData,
    language,
    averageRating,
    totalAmount
  });

  useEffect(() => {
    const container = containerRef?.current;
    if (!container) return;

    const updateDimensions = () => {
      const { height, width } = container.getBoundingClientRect();
      setDimensions({
        height: Math.floor(height),
        width: Math.floor(width)
      });
    };

    window.addEventListener("resize", updateDimensions);
    updateDimensions();

    return () => window.removeEventListener("resize", updateDimensions);
  }, []);

  if (!arrayData) return <></>;

  const slices = transformFunc(arrayData || [], t);

  const pieWidth = Math.floor(dimensions.width * 0.4);
  const legendWidth = Math.floor(dimensions.width * 0.5);
  const fontSize = Math.max(16, dimensions.width * 0.04);

  const CenteredMetric = ({ centerX, centerY }) => {
    let displayValue = null;

    if (averageRating) {
      displayValue = (
        <>
          {language === "de" && "Ø "}
          {formatIntlNumber(averageRating, { language, points: 1 })}
        </>
      );
    } else if (totalAmount) {
      displayValue = formatIntlNumber(totalAmount, { language });
    }

    if (!averageRating && !totalAmount) return <></>;

    return (
      <text
        x={centerX}
        y={centerY}
        textAnchor="middle"
        dominantBaseline="central"
        style={{
          fontSize: `${parseFloat(fontSize).toFixed(2)}px`,
          fontWeight: 600
        }}
      >
        {displayValue}
      </text>
    );
  };

  const TooltipComponent = ({ datum }) => {
    const total = arrayData.reduce((sum, item) => sum + item.value, 0);
    const percentage = ((datum.value / total) * 100).toFixed(2);

    return (
      <TooltipBox>
        <strong>{datum.label}</strong>
        {averageRating && (
          <span>
            {t ? t("Total rating") : "Gesamtbewertung"}: {percentage}%
          </span>
        )}
      </TooltipBox>
    );
  };

  return (
    <ThemeProvider>
      <PieContainer ref={containerRef} styling={styling} pieWidth={pieWidth}>
        <ResponsivePie
          data={slices}
          margin={{ left: 10, right: 10, top: 20, bottom: 20 }}
          height={dimensions.height}
          width={pieWidth}
          activeOuterRadiusOffset={8}
          colors={{ datum: "data.color" }}
          arcLabelsSkipAngle={1}
          arcLabelsTextColor="#ffffff"
          enableArcLinkLabels={false}
          tooltip={TooltipComponent}
          layers={["arcs", "arcLabels", "arcLinkLabels", CenteredMetric]}
          innerRadius={0.6}
        />
        <SvgWrapper
          height={dimensions.height}
          width={legendWidth}
          margin={{ left: 0, right: 0, top: 0, bottom: 0 }}
        >
          <BoxLegendSvg
            data={slices}
            anchor="left"
            containerHeight={dimensions.height}
            direction="column"
            // item specific
            itemWidth={200}
            itemHeight={30}
            symbolShape="circle"
          />
        </SvgWrapper>
      </PieContainer>
    </ThemeProvider>
  );
};
